// C-F6 seed: the metro PIN list (the 8 metros, N5), three batches cut at 09:30, one group, and the classification
// workflow of plan v2 §4.2 (live): metro PIN? → city tag; salaried? → emp tag; hot-lead score ≥ 70 → score:hot and
// the "Hot leads" batch; then metro + salaried → "Metro salaried", non-metro → "Non-metro".
// seedClassification() runs it on the seed's leads, as if each had arrived on its own day, and makes the cuts.
import type { State, VersionRecord, WorkflowRecord } from '../state'
import { cutBatches } from '../rules/batches'
import { classifyLead } from '../rules/classify'
import { PIN_PLACES } from '../rules/registry-fields'
import { toDocument, type DemoEdge, type DemoNode } from './workflows'

export const SEED_LISTS: State['lists'] = [
  {
    id: 'list-metro-1',
    list_key: 'lk-metro',
    name: 'Metro PIN list',
    version: 1,
    count: 8,
    first: PIN_PLACES.slice(0, 5).map(([prefix]) => prefix + '001'),
    prefixes: PIN_PLACES.slice(0, 8).map(([prefix]) => prefix),
    made_by: 'u-admin',
    at: '2026-10-20',
  },
]

export const SEED_BATCHES: State['batches'] = [
  { id: 'b-metro-sal', name: 'Metro salaried', cut_time: '09:30', made_by: 'u-admin', at: '2026-10-20' },
  { id: 'b-hot', name: 'Hot leads', cut_time: '09:30', made_by: 'u-admin', at: '2026-10-20' },
  { id: 'b-non-metro', name: 'Non-metro', cut_time: '09:30', made_by: 'u-admin', at: '2026-10-20' },
]

export const SEED_GROUPS: State['groups'] = [
  {
    id: 'g-metro-or-hot',
    name: 'Metro salaried or hot',
    rule: { any_of: ['b-metro-sal', 'b-hot'], all_of: [], minus: [] },
    made_by: 'u-admin',
    at: '2026-10-21',
  },
]

export const CLASSIFY_WORKFLOW: WorkflowRecord = {
  id: 'wf-sort',
  name: 'Sort new leads',
  kind: 'classification',
}

const NODES: DemoNode[] = [
  ['k1', 'trigger.new_data', 'New data (any source)', { when: 'arrival', sources: 'any', hour: 8 }],
  ['k2', 'classify.list_check', 'PIN in the metro list?', { list: 'lk-metro' }],
  ['k3', 'classify.add_tag', 'Tag city:metro', { tag: 'city:metro' }],
  ['k4', 'classify.add_tag', 'Tag city:non-metro', { tag: 'city:non-metro' }],
  ['k5', 'classify.field_check', 'Salaried?', { field: 'employment', values: 'salaried' }],
  ['k6', 'classify.add_tag', 'Tag emp:salaried', { tag: 'emp:salaried' }],
  ['k7', 'classify.add_tag', 'Tag emp:self-employed', { tag: 'emp:self-employed' }],
  [
    'k8',
    'classify.score',
    'Hot-lead score ≥ 70?',
    { rules: 'emp:salaried=30, city:metro=20, returned_7d=20, clicked_link=30', threshold: 70 },
  ],
  ['k9', 'classify.add_tag', 'Tag score:hot', { tag: 'score:hot' }],
  ['k10', 'classify.put_in_batch', 'Batch: Hot leads', { batch: 'b-hot' }],
  ['k11', 'classify.field_check', 'Metro?', { field: 'tag', values: 'city:metro' }],
  ['k12', 'classify.field_check', 'Metro and salaried?', { field: 'tag', values: 'emp:salaried' }],
  ['k13', 'classify.put_in_batch', 'Batch: Metro salaried', { batch: 'b-metro-sal' }],
  ['k14', 'classify.put_in_batch', 'Batch: Non-metro', { batch: 'b-non-metro' }],
  ['k15', 'classify.end', 'End', {}],
]

const EDGES: DemoEdge[] = [
  ['k1', 'k2'],
  ['k2', 'k3', 'yes'],
  ['k2', 'k4', 'no'],
  ['k3', 'k5'],
  ['k4', 'k5'],
  ['k5', 'k6', 'yes'],
  ['k5', 'k7', 'no'],
  ['k6', 'k8'],
  ['k7', 'k8'],
  ['k8', 'k9', 'yes'],
  ['k8', 'k11', 'no'],
  ['k9', 'k10'],
  ['k10', 'k11'],
  ['k11', 'k12', 'yes'],
  ['k11', 'k14', 'no'],
  ['k12', 'k13', 'yes'],
  ['k12', 'k15', 'no'],
  ['k13', 'k15'],
  ['k14', 'k15'],
]

export function seedClassifyVersions(): VersionRecord[] {
  return [
    {
      id: 'v-5',
      workflow_id: CLASSIFY_WORKFLOW.id,
      version: 1,
      status: 'live',
      published_by: 'u-admin',
      published_at: '2026-10-21',
      document: toDocument(NODES, EDGES),
    },
  ]
}

/**
 * Sorts the seed's leads (no API call counted), each joining its batches on the day it came in, then closes the
 * cuts of the day before and of this morning (09:30, before the start at 10:00): new people make tomorrow's slice.
 */
export function seedClassification(state: State): void {
  const version = state.versions.find((v) => v.id === 'v-5')!
  // the seed's records get their own ids, so the id counter (run ids the tests name) starts where it did before
  const nextId = state.nextId
  for (const lead of state.leads) {
    if (!classifyLead(state, lead, version, false)) continue
    for (const member of state.members) if (member.lead_code === lead.lead_code) member.added = lead.received
  }
  state.classifyRuns.forEach((run) => {
    const lead = state.leads.find((l) => l.lead_code === run.lead_code)!
    run.at = `${lead.received}T08:00:00+05:30`
    run.trace.forEach((step) => (step.at = run.at))
  })
  cutBatches(state, '2026-10-23', '2026-10-23T09:30:00+05:30')
  cutBatches(state, '2026-10-24', '2026-10-24T09:30:00+05:30')
  state.classifyRuns.forEach((run, i) => (run.id = `cr-seed-${i + 1}`))
  const cutIds = new Map(state.cuts.map((cut, i) => [cut.id, `cut-seed-${i + 1}`]))
  state.cuts.forEach((cut) => (cut.id = cutIds.get(cut.id)!))
  state.members.forEach((m) => (m.cut_id = m.cut_id && cutIds.get(m.cut_id)!))
  state.nextId = nextId
}
